import { showBackdrop, hideBackdrop } from "./backdrop-utils.js";
import {
  focusModalPanel,
  getOpenModal,
  restoreModalTriggerFocus,
  setAriaHidden,
  setBodyLocked,
  storeModalTrigger
} from "./modal-utils.js";

const OPEN_CLASS = "video-modal--open";
const BACKDROP_KEY = "video-modal";
const MODAL_SELECTOR = ".video-modal";
const PANEL_SELECTOR = ".video-modal__panel";

/**
 * @param {string} raw
 * @returns {string | null}
 */
const videoUrlToEmbedSrc = (raw) => {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "player.vimeo.com") {
      return `${url.origin}${url.pathname}${url.search}`;
    }

    if (host === "vimeo.com" || host.endsWith(".vimeo.com")) {
      const parts = url.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      if (last && /^\d+$/.test(last)) {
        return `https://player.vimeo.com/video/${last}${url.search}`;
      }
      return null;
    }

    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id.split("?")[0]}` : null;
    }

    if (host === "m.youtube.com" || host.includes("youtube.com")) {
      if (url.pathname.startsWith("/embed/")) {
        return `${url.origin}${url.pathname}${url.search}`;
      }
      if (url.pathname.startsWith("/shorts/")) {
        const id = url.pathname.split("/").filter(Boolean)[1];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      const v = url.searchParams.get("v");
      return v ? `https://www.youtube.com/embed/${v}` : null;
    }
  } catch {
    return null;
  }

  return null;
};

/**
 * Добавляет параметры автозапуска к уже собранному embed-URL (клик по play считается жестом пользователя).
 * @param {string} embedSrc
 * @returns {string}
 */
const withAutoplayParams = (embedSrc) => {
  try {
    const url = new URL(embedSrc);
    const host = url.hostname.replace(/^www\./, "");

    url.searchParams.set("autoplay", "1");

    if (host.includes("youtube.com")) {
      url.searchParams.set("playsinline", "1");
    }

    return url.toString();
  } catch {
    return embedSrc;
  }
};

const openVideoModal = (modal, documentReference, iframe, embedSrc, trigger) => {
  iframe.setAttribute("src", withAutoplayParams(embedSrc));
  modal.removeAttribute("hidden");
  modal.classList.add(OPEN_CLASS);
  setAriaHidden(modal, false);
  setBodyLocked(documentReference, true);
  showBackdrop(BACKDROP_KEY, documentReference);
  focusModalPanel(modal, PANEL_SELECTOR);
  storeModalTrigger(modal, trigger, "__videoModalLastTrigger");
};

const closeVideoModal = (modal, documentReference, iframe) => {
  iframe.removeAttribute("src");
  modal.classList.remove(OPEN_CLASS);
  modal.setAttribute("hidden", "");
  setAriaHidden(modal, true);
  setBodyLocked(documentReference, false);
  hideBackdrop(BACKDROP_KEY, documentReference);
  restoreModalTriggerFocus(modal, "__videoModalLastTrigger");
};

export const initVideoModal = (documentReference = document) => {
  const modal = documentReference.getElementById("video-modal");
  const iframe = modal?.querySelector(".video-modal__iframe");

  if (!modal || !iframe || !modal.classList.contains("video-modal")) {
    return;
  }

  documentReference.addEventListener("click", (event) => {
    const host = event.target.closest("[data-video-url]");
    if (host) {
      const raw = host.getAttribute("data-video-url")?.trim();
      const embedSrc = raw ? videoUrlToEmbedSrc(raw) : null;
      if (!embedSrc) {
        return;
      }

      event.preventDefault();
      openVideoModal(modal, documentReference, iframe, embedSrc, host);
      return;
    }

    if (event.target.closest("[data-video-modal-close]")) {
      const modalToClose = event.target.closest(".video-modal");
      if (modalToClose?.classList.contains(OPEN_CLASS)) {
        closeVideoModal(modalToClose, documentReference, iframe);
      }
      return;
    }

    if (event.target.classList.contains("backdrop")) {
      const open = getOpenModal(documentReference, MODAL_SELECTOR, OPEN_CLASS);
      if (open) {
        closeVideoModal(open, documentReference, iframe);
      }
    }
  });

  documentReference.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      const open = getOpenModal(documentReference, MODAL_SELECTOR, OPEN_CLASS);
      if (open) {
        closeVideoModal(open, documentReference, iframe);
      }
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    const host = event.target.closest("[data-video-url]");
    if (!host || event.target !== host) {
      return;
    }

    const raw = host.getAttribute("data-video-url")?.trim();
    const embedSrc = raw ? videoUrlToEmbedSrc(raw) : null;
    if (!embedSrc) {
      return;
    }

    event.preventDefault();
    openVideoModal(modal, documentReference, iframe, embedSrc, host);
  });
};
